import { supabase } from "./supabase";

import imageCompression from "browser-image-compression";

export const IMAGE_MAX_BYTES = 500 * 1024;
export const AUDIO_MAX_BYTES = 2 * 1024 * 1024;
export const AUDIO_MAX_MS = 90000;
const OPTS = { maxWidthOrHeight: 1024, initialQuality: 0.7, maxSizeMB: 0.45, useWebWorker: true, fileType: "image/jpeg", alwaysKeepResolution: false };

export const fmtBytes = (n) => (n >= 1024 * 1024 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

const isImage = (f) => /^image\//.test(f.type) || /\.(heic|heif|jpe?g|png|webp|gif|bmp|avif)$/i.test(f.name || "");

// Compress to JPEG ≤1024px, aiming ≤450 KB; one lower-quality retry if still > 500 KB. Returns { blob, before, after, warn }.
export const compressImage = async (file) => {
  if (!isImage(file)) throw Object.assign(new Error("not_image"), { code: "not_image" });
  let blob;
  try { blob = await imageCompression(file, OPTS); }
  catch (e) { throw Object.assign(new Error("compress_failed"), { code: "compress_failed", cause: e }); }
  let warn = false;
  if (blob.size > IMAGE_MAX_BYTES) {
    blob = await imageCompression(blob, { ...OPTS, initialQuality: 0.5, maxSizeMB: 0.4 }).catch(() => blob);
    warn = blob.size > IMAGE_MAX_BYTES;
  }
  const out = new Blob([blob], { type: "image/jpeg" });
  return { blob: out, before: file.size, after: out.size, warn };
};

// Backwards-compatible helper used by older call sites.
export const resizeImage = (file) => compressImage(file).then((r) => r.blob);

// Voice-optimised recorder MIME selection (Opus preferred, AAC on Safari/iOS).
export const pickAudioMime = () => {
  const c = [["audio/webm;codecs=opus", 24000, "webm"], ["audio/ogg;codecs=opus", 24000, "ogg"], ["audio/mp4", 32000, "m4a"], ["audio/webm", 24000, "webm"]];
  return c.find(([m]) => typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported(m)) || [undefined, 24000, "webm"];
};
export const audioExt = (blob) => (/ogg/.test(blob.type) ? "ogg" : /mp4|aac|m4a/.test(blob.type) ? "m4a" : "webm");

export const uploadToBucket = async (bucket, userId, blob, ext) => {
  const path = `${userId}/${crypto.randomUUID()}.${ext}`;
  const { error } = await supabase.storage.from(bucket).upload(path, blob, { contentType: blob.type, upsert: false });
  if (error) throw error;
  return path;
};

export const signedUrl = async (bucket, path, seconds = 3600) => {
  const { data } = await supabase.storage.from(bucket).createSignedUrl(path, seconds);
  return data?.signedUrl ?? null;
};
